import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import Modal from '../components/Modal';
import { useLanguage } from '../i18n/LanguageProvider';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Knowledge = ({ clinic, onLogout }) => {
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Modal states
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    confirmText: 'OK',
    showCancel: false
  });

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/knowledge/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
    setMessage('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage(t('knowledge.pleaseSelectFile'));
      setMessageType('error');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/knowledge/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage(t('knowledge.fileUploaded'));
      setMessageType('success');
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      fetchFiles();
    } catch (error) {
      setMessage(error.response?.data?.error || t('knowledge.failedToUpload'));
      setMessageType('error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (fileId) => {
    setModal({
      isOpen: true,
      title: t('modal.confirm'),
      message: t('knowledge.confirmDeleteFile'),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_BASE_URL}/api/knowledge/files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          setMessage(t('knowledge.fileDeleted'));
          setMessageType('success');
          fetchFiles();
        } catch (error) {
          setMessage(error.response?.data?.error || t('knowledge.failedToDeleteFile'));
          setMessageType('error');
        }
      },
      confirmText: t('common.delete'),
      showCancel: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-semibold text-gray-900 hover:text-gray-700">
                 {t('nav.clinicAssistant')}
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <span className="text-gray-700">{clinic?.clinic_name}</span>
              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
              >
                {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <Link
              to="/dashboard"
              className="text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('knowledge.backToDashboard')}
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('knowledge.uploadKnowledgeBaseFiles')}
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('knowledge.selectFile')}
              </label>
              <input
                id="file-input"
                type="file"
                accept=".pdf,.txt"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              {selectedFile && (
                <div className="mt-2 text-sm text-gray-600">
                  {t('knowledge.selected')}: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {uploading ? t('knowledge.uploading') : t('knowledge.uploadFile')}
            </button>

            {message && (
              <div className={`mt-4 p-4 rounded-md ${
                messageType === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {message}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {t('knowledge.uploadedFiles')}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {files.length === 0 ? (
                <div className="px-6 py-4 text-gray-500 text-center">
                  {t('knowledge.noFilesUploaded')}
                </div>
              ) : (
                files.map((file) => (
                  <div key={file.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {file.filename}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t('knowledge.uploaded')}: {new Date(file.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(file.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        confirmText={modal.confirmText}
        showCancel={modal.showCancel}
      />
    </div>
  );
};

export default Knowledge;